/* Datahjälpare för KanAI-chatten: verktygsetiketter, gruppering,
   persistensformat (compactToolCalls) och extraktion av dokumentkort,
   källor och fakturasökningar ur toolCalls. */
import { useEffect, useState } from "react";
import { resolveApiUrl } from "../../apiBase";
import { getActiveTenantSlug } from "../../api";
import { dagensDatum, tolkaTidpunkt } from "../../utils/tidsstampel";

const apiBaseUrl = resolveApiUrl();

/* Nedladdnings-/highlight-URL:er öppnas ibland utanför appens fetch-lager
   (ny flik via modalens "Öppna i ny flik" eller mittenklick) — då följer
   X-Tenant-Slug-headern inte med. Backend accepterar tenant_slug som
   query-param (samma membership-kontroll), så den bakas in i URL:en. */
function withTenantSlug(url) {
  if (!url) return url;
  const slug = getActiveTenantSlug();
  if (!slug || url.includes("tenant_slug=")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}tenant_slug=${encodeURIComponent(slug)}`;
}

/* ══ Tool name labels ══ */
const TOOL_LABELS = {
  search_knowledge: "Söker i kunskapsbasen",
  get_project_overview: "Hämtar projektöversikt",
  get_project_economy: "Hämtar projektets ekonomi",
  get_project_issues: "Hämtar ärenden",
  get_project_documents: "Hämtar dokument",
  get_project_diary: "Läser dagbok",
  get_project_safety: "Hämtar skyddsronder",
  get_project_quality: "Hämtar kvalitetsdata",
  get_ata_overview: "Hämtar ÄTA-logg",
  get_meetings_overview: "Hämtar mötesprotokoll",
  search_suppliers: "Söker leverantörer",
  get_resource_overview: "Hämtar resursöversikt",
  get_economy_overview: "Hämtar koncernekonomin",
  get_tender_overview: "Hämtar anbudsöversikt",
  read_document_content: "Läser dokument",
  create_document_draft: "Skapar dokumentutkast",
  create_todo: "Skapar uppgift",
  create_issue: "Skapar ärende",
  create_diary_entry: "Skapar dagboksanteckning",
  update_issue_status: "Uppdaterar ärende",
  update_todo_status: "Uppdaterar uppgift",
  create_ata_draft: "Skapar ÄTA-utkast",
  create_meeting_note: "Skapar mötesanteckning",
  send_notification: "Skickar notifikation",
  create_safety_observation: "Loggar skyddsobservation",
  search_invoices_by_supplier: "Söker fakturor från leverantör",
  get_supplier_spend_in_project: "Summerar kostnader per leverantör",
  get_top_customers: "Hämtar topp-kunder",
  get_vehicle_tco: "Beräknar totalkostnad för fordon",
};

/* ── Statusrad: EN text som uppdateras in place medan KanAI arbetar ── */
const STATUS_ARG_KEYS = ["query", "project_query", "question", "search", "name", "title", "text"];

function statusTextFor(activeTool, toolCalls) {
  if (activeTool && activeTool.name) {
    const label = TOOL_LABELS[activeTool.name] || "Hämtar underlag";
    const args = activeTool.args || {};
    const key = STATUS_ARG_KEYS.find((k) => typeof args[k] === "string" && args[k].trim());
    const detail = key ? args[key].trim() : "";
    return detail ? `${label}: ${detail.length > 60 ? detail.slice(0, 57) + "..." : detail}` : label;
  }
  if (Array.isArray(toolCalls) && toolCalls.length > 0) return "Sammanställer svar";
  return "KanAI tänker";
}

const SUGGESTIONS = [
  "Vilka arbetsmiljörisker gäller vid sprängning?",
  "Sammanfatta pågående ÄTA-ärenden i mina projekt",
  "Vilka policyer gäller för avvikelsehantering?",
  "Hur ser orderstock och prognoser ut just nu?",
];

/* ── Wait messages ── */
const WAIT_MESSAGES = [
  "KanAI tänker...",
  "Söker i era styrdokument...",
  "Analyserar frågan...",
  "Sammanställer svar...",
  "Granskar relevanta policyer...",
];

function useRotatingMessage(isActive, intervalMs) {
  if (intervalMs === undefined) intervalMs = 3500;
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!isActive) { setMessage(""); return; }
    let idx = Math.floor(Math.random() * WAIT_MESSAGES.length);
    setMessage(WAIT_MESSAGES[idx]);
    const id = setInterval(() => { idx = (idx + 1) % WAIT_MESSAGES.length; setMessage(WAIT_MESSAGES[idx]); }, intervalMs);
    return () => clearInterval(id);
  }, [isActive, intervalMs]);
  return message;
}

/* ── Group conversations by date ──
   Returnerar { pinned, ordered } där ordered är en array av
   { key, label, items } i visnings-ordning, från nyast till äldst.
*/
const SE_MONTHS = [
  "Januari", "Februari", "Mars", "April", "Maj", "Juni",
  "Juli", "Augusti", "September", "Oktober", "November", "December",
];

/* Grupperingen räknar svenska kalenderdagar (ÅÅÅÅ-MM-DD i Europe/Stockholm),
   oavsett webbläsarens tidszon. */
function svenskDagDelar(d) {
  const s = dagensDatum(d);
  return { ar: Number(s.slice(0, 4)), man: Number(s.slice(5, 7)) - 1, dagNr: Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10))) / 86400000 };
}

function groupConversations(convs) {
  const nu = svenskDagDelar(new Date());
  const forraManad = nu.man === 0 ? { ar: nu.ar - 1, man: 11 } : { ar: nu.ar, man: nu.man - 1 };

  const pinned = [];
  const buckets = new Map();
  function ensure(key, label) {
    if (!buckets.has(key)) buckets.set(key, { key, label, items: [] });
    return buckets.get(key);
  }

  convs.forEach((c) => {
    if (c.pinned) { pinned.push(c); return; }
    const d = svenskDagDelar((c.updatedAt && tolkaTidpunkt(c.updatedAt)) || new Date(0));
    const dagarSedan = nu.dagNr - d.dagNr;
    if (dagarSedan <= 0) ensure("today", "Idag").items.push(c);
    else if (dagarSedan === 1) ensure("yesterday", "Igår").items.push(c);
    else if (dagarSedan <= 7) ensure("last7d", "Senaste 7 dagarna").items.push(c);
    else if (dagarSedan <= 14) ensure("lastWeek", "Förra veckan").items.push(c);
    else if (d.ar === nu.ar && d.man === nu.man) ensure("thisMonth", "Denna månad").items.push(c);
    else if (d.ar === forraManad.ar && d.man === forraManad.man) ensure("lastMonth", "Förra månaden").items.push(c);
    else if (d.ar === nu.ar) {
      ensure(`month-${d.ar}-${String(d.man).padStart(2, "0")}`, SE_MONTHS[d.man]).items.push(c);
    }
    else if (d.ar === nu.ar - 1) ensure("lastYear", "Förra året").items.push(c);
    else ensure(`year-${d.ar}`, String(d.ar)).items.push(c);
  });

  const ordered = [];
  ["today", "yesterday", "last7d", "lastWeek", "thisMonth", "lastMonth"].forEach((k) => {
    if (buckets.has(k)) ordered.push(buckets.get(k));
  });
  Array.from(buckets.keys())
    .filter((k) => k.startsWith("month-"))
    .sort((a, b) => b.localeCompare(a))
    .forEach((k) => ordered.push(buckets.get(k)));
  if (buckets.has("lastYear")) ordered.push(buckets.get("lastYear"));
  Array.from(buckets.keys())
    .filter((k) => k.startsWith("year-"))
    .sort((a, b) => b.localeCompare(a))
    .forEach((k) => ordered.push(buckets.get(k)));

  return { pinned, ordered };
}

const _DEFAULT_EXPANDED_GROUPS = new Set(["pinned", "today", "yesterday", "last7d"]);
function isGroupExpandedDefault(key) {
  return _DEFAULT_EXPANDED_GROUPS.has(key);
}

function formatFileSize(sizeBytes) {
  if (!sizeBytes || Number(sizeBytes) <= 0) return "";
  if (sizeBytes >= 1048576) return `${(sizeBytes / 1048576).toFixed(1).replace(".", ",")} MB`;
  if (sizeBytes >= 1024) return `${Math.round(sizeBytes / 1024)} KB`;
  return `${sizeBytes} B`;
}

function fileGroupFromName(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOCX";
  if (["xls", "xlsx"].includes(ext)) return "XLSX";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "Bilder";
  return "Övrigt";
}

const _GENERATED_ARTIFACT_TOOLS = new Set([
  "create_document_draft",
  "create_spreadsheet_draft",
]);

/**
 * Kompakt spar av vilka verktyg ett svar faktiskt korde. Utan detta har KanAI
 * inget minne av att den skapat en fil nasta gang konversationen fortsatter,
 * och kan bade upprepa obelagda pastaenden och ta tillbaka nagot den verkligen
 * gjort. Bara falten som behovs sparas — inte hela resultatobjekten.
 */
/* Läsverktyg svarar utan "success"-fält; de räknades förut som misslyckade
 * och KanAI tog i följdfrågan tillbaka korrekta svar (stagetest 2026-10-27).
 * Lyckat = success:true, eller varken success eller error i svaret. */
function verktygetLyckades(result) {
  if (!result || typeof result !== "object") return false;
  if (result.success !== undefined) return Boolean(result.success);
  return !result.error;
}

function compactToolCalls(msg) {
  const calls = Array.isArray(msg?.toolCalls) ? msg.toolCalls : null;
  if (!calls || !calls.length) return undefined;
  const compact = calls.slice(0, 20).map((tc) => {
    const entry = {
      name: tc?.name,
      ok: verktygetLyckades(tc?.result),
      documentId: tc?.result?.document_id || undefined,
      documentName: tc?.result?.name || undefined,
    };
    // Bevara result-delmängden som extractGeneratedArtifacts/
    // extractDocumentSourceCitations behöver — annars försvinner
    // dokumentkort, källor och filpanelens innehåll när en sparad
    // konversation öppnas igen.
    if (_GENERATED_ARTIFACT_TOOLS.has(tc?.name) && tc?.result?.success && tc?.result?.document_id) {
      entry.result = {
        success: true,
        document_id: tc.result.document_id,
        name: tc.result.name || tc?.args?.title || undefined,
      };
    } else if (_DOCUMENT_SEARCH_TOOLS.has(tc?.name) && Array.isArray(tc?.result?.results)) {
      entry.result = {
        results: tc.result.results.slice(0, 30).map((r) => ({
          source_id: r?.source_id,
          document_id: r?.document_id,
          document_name: r?.document_name,
          project_name: r?.project_name,
          page: r?.page,
          section_title: r?.section_title,
          snippet: r?.snippet,
          similarity: r?.similarity,
          highlight_url: r?.highlight_url,
          download_url: r?.download_url,
        })),
      };
    }
    return entry;
  }).filter((tc) => tc.name);
  return compact.length ? compact : undefined;
}

function extractGeneratedArtifacts(toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return [];
  return toolCalls
    // Andra villkoret är bakåtkompatibilitet: konversationer sparade före
    // 2026-08-18 har bara kompaktformatet (ok/documentId/documentName).
    .filter((tc) => _GENERATED_ARTIFACT_TOOLS.has(tc?.name)
      && ((tc?.result?.success && tc?.result?.document_id) || (tc?.ok && tc?.documentId)))
    .map((tc, index) => {
      const docId = tc?.result?.document_id || tc.documentId;
      return {
        id: `generated-${docId}-${index}`,
        documentId: docId,
        name: tc?.result?.name || tc?.args?.title || tc?.documentName || `Dokument ${docId}`,
        source: "kanai",
        size_bytes: null,
        download_url: `${apiBaseUrl}/api/kanai/documents/${encodeURIComponent(String(docId))}/download`,
      };
    });
}

const _DOCUMENT_SEARCH_TOOLS = new Set([
  "search_project_documents",
  "search_documents_across_projects",
  "search_knowledge", // AMA, MER, AB/ABT, AFS med sida + markering (fas D 2026-10-15)
]);

function extractDocumentSourceCitations(toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return [];
  const citations = [];
  toolCalls.forEach((tc, tcIdx) => {
    if (!_DOCUMENT_SEARCH_TOOLS.has(tc?.name)) return;
    const results = tc?.result?.results;
    if (!Array.isArray(results)) return;
    results.forEach((r, idx) => {
      if (!r?.highlight_url) return;
      if (!r.document_id) {
        // Kunskapsbasens standarder: ingen document_id, men PDF-syskon + sida
        const namn = String(r.source || "").split("/").pop() || "Standard";
        citations.push({
          id: `cite-${tcIdx}-${idx}`,
          sourceId: r.source_id || `K${idx + 1}`,
          documentId: null,
          documentName: namn.replace(/\.md$/i, ".pdf"),
          projectName: r.kalla_typ || null,
          page: r.page,
          sectionTitle: r.section,
          snippet: (r.text || "").slice(0, 240),
          similarity: r.score,
          highlightUrl: `${apiBaseUrl}${r.highlight_url}`,
          downloadUrl: null,
        });
        return;
      }
      citations.push({
        id: `cite-${tcIdx}-${idx}`,
        sourceId: r.source_id || `S${idx + 1}`,
        documentId: r.document_id,
        documentName: r.document_name || `Dokument ${r.document_id}`,
        projectName: r.project_name,
        page: r.page,
        sectionTitle: r.section_title,
        snippet: r.snippet,
        similarity: r.similarity,
        highlightUrl: `${apiBaseUrl}${r.highlight_url}`,
        downloadUrl: r.download_url ? `${apiBaseUrl}${r.download_url}` : null,
      });
    });
  });
  return citations;
}

/**
 * Plocka ut resultat från search_invoices_by_supplier-tool-anrop i ett
 * meddelandes toolCalls. Returnerar en array (kan vara flera anrop i
 * samma meddelande om LLM:en kört flera sökningar).
 */
function extractInvoiceSearches(toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return [];
  return toolCalls
    .filter((tc) => tc?.name === "search_invoices_by_supplier" && tc?.result && !tc.result.error)
    .map((tc, index) => ({ id: `inv-search-${index}`, result: tc.result }));
}

/* ── Bilagor (temp-uppladdade filer) ──────────────────────────────
   Filerna lagras permanent på samma dokumentlager som resten av
   plattformen — de är "temporära" bara i namnet. Normaliseras här till
   samma form som filpanelen och dokumentmodalen förväntar sig, så att
   en återupptagen konversation kan öppna sina bilagor igen. */
function toAttachment(doc) {
  if (!doc?.id) return null;
  const id = String(doc.id);
  return {
    id,
    name: doc.name || `Dokument ${id}`,
    size_bytes: doc.size_bytes ?? null,
    source: "upload",
    download_url: doc.download_url
      || `${apiBaseUrl}/api/kanai/documents/${encodeURIComponent(id)}/download`,
  };
}

/* Samlar ihop bilagorna från en konversations alla meddelanden till en
   deduplicerad lista — den blir chattens aktiva bilagor när en sparad
   konversation öppnas igen, så följdfrågor får samma underlag. */
function collectAttachments(messages) {
  const seen = new Set();
  const out = [];
  (Array.isArray(messages) ? messages : []).forEach((m) => {
    (Array.isArray(m?.attachments) ? m.attachments : []).forEach((a) => {
      const att = toAttachment(a);
      if (!att || seen.has(att.id)) return;
      seen.add(att.id);
      out.push(att);
    });
  });
  return out;
}

export {
  toAttachment,
  collectAttachments,
  TOOL_LABELS,
  statusTextFor,
  SUGGESTIONS,
  useRotatingMessage,
  groupConversations,
  isGroupExpandedDefault,
  formatFileSize,
  fileGroupFromName,
  compactToolCalls,
  extractGeneratedArtifacts,
  extractDocumentSourceCitations,
  extractInvoiceSearches,
  withTenantSlug,
};
